import { Component, OnInit, HostBinding, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderStateService } from '../../../order/services/order-state.service';
import { Order } from '../../../order/models/order';
import { OrderLine } from '../../../order/models/order-line';
import { environment } from '../../../../../environments/environment';
import { PartnerStateService } from '../../services/partner-state.service';
import { PartnerStateEnum } from '../../services/partner-state.enum';
import { OrderTypeEnum } from '../../../order/models/order-type.enum';
import { BookingSlot } from '../../../booking/models/booking-slot';
import { DiscountCoupon } from '../../../payment/models/discount-coupon';
import { BookingSlotChangeStateService } from '../../services/booking-slot-change-state-service.service';
import { MatDialog } from '@angular/material/dialog';
import { MessageComponent } from '../../../general/components/message/message.component';
import { LoadingSpinnerService } from '../../../../services/loading-spinner.service';
import { LoggerService } from '../../../../services/logger/logger.service';
import { ShopSettingsService } from '../../../general/services/shop-settings.service';
import { Observable, of, pipe, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { LocalizationService } from '../../../../services/localization.service';
import { NotificationService } from '../../../general/services/notification.service';
import { Partner } from '../../models/partner';

@Component({
  selector: 'app-order-details',
  templateUrl: './order-details.component.html',
  styleUrls: ['./order-details.component.css']
})
export class OrderDetailsComponent implements OnInit, OnDestroy {

  @HostBinding('class') classes = 'orders-detail';

  orderId: number;
  showSlots = false;
  actionInProgress = false;
  slotMoveResult = '';

  private _order: Order;
  private refundOrderSubscription: Subscription;

  constructor(
    private activateRoute: ActivatedRoute,
    private orderStateService: OrderStateService,
    private partnerStateService: PartnerStateService,
    public bookingSlotChangeStateService: BookingSlotChangeStateService,
    private loadingSpinnerService: LoadingSpinnerService,
    private logger: LoggerService,
    private dialog: MatDialog,
    private shopSettingsService: ShopSettingsService,
    private localizationService: LocalizationService,
    private notificationService: NotificationService,
    private router: Router,
  ) {
    this.orderId = activateRoute.snapshot.params['orderid'];
  }

  ngOnInit() {
    this.loadingSpinnerService.show();
    this.shopSettingsService.initialize().subscribe(
      shopSettings => {
        this.logger.info(`BookingStateService: initialize shop settings received.`);
        this._loadOrder().subscribe(
          res => {
            this.loadingSpinnerService.hide();
          }
        );
      },
      error2 => {
        this.logger.info(`BookingStateService: initialize shop settings receive error ${error2}.`);
        this.loadingSpinnerService.hide();
      }
    );
  }

  ngOnDestroy() {
    this.notificationService.hide(OrderDetailsComponent.name);
    if (this.refundOrderSubscription) {
      this.refundOrderSubscription.unsubscribe();
      this.refundOrderSubscription = null;
    }
  }

  private _loadOrder(): Observable<boolean> {
    return this.orderStateService.loadOrder(this.orderId).pipe(map(
      result => {
        this._order = this.orderStateService.order;
        return result;
      })
    );
  }

  get name(): string {
    if (!this._order) {
      return '';
    }
    return this._order.name;
  }

  get bookingDate(): string {
    if (!this._order) {
      return '';
    }
    return this.localizationService.formatDateToShotWeekdayAndDate(this._order.date);
  }

  get flightDate(): string {
    if (!this._order) {
      return '';
    }
    return this._order.bookingDate.toDateString();
  }

  get boardingTime(): string {
    if (!this._order || !this._order.timeSlotName || this._order.orderType === OrderTypeEnum.Giftcard) {
      return '';
    }

    return this._order.timeSlotName;
  }

  get flightDuration(): string {
    if (!this._order || !this._order.minutes) {
      return '';
    }
    return this._order.minutes + '';
  }

  get orderLines(): OrderLine[] {
    if (!this._order) {
      return [];
    }

    return this._order.orderLines;
  }

  toMoneyStr(x: number): string {
    return environment.globalSettings.toMoneyStr(x);
  }

  onBackwardClick() {
    this.partnerStateService.update(PartnerStateEnum.ShowOrders);
  }

  get coupons(): DiscountCoupon[] {
    return this._order ? this._order.coupons : new Array<DiscountCoupon>();
  }

  get canChangeTimeSlot(): boolean {
    // return false;
    return this._order && this._order.canChangeTimeSlot;
  }

  get isBirthday(): boolean {
    return this._order.orderType === OrderTypeEnum.Birthday;
  }

  get isGroup(): boolean {
    return this._order.orderType === OrderTypeEnum.Group;
  }

  get isEvent(): boolean {
    return this.isBirthday || this.isGroup;
  }

  get isSingle(): boolean {
    return this._order.orderType === OrderTypeEnum.Single;
  }

  get canBeRefunded(): boolean {
    const now = new Date();
    return this._order && this._order.state === 'done' && this._order.totalAmount !== 0
      && this.shopSettingsService.shopSettings.partnerCodePayment
      && !this._order.isGiftCardType
      && !this._order.isMultiOrder
      && this._order.bookingDate && now < this._order.bookingDate;
  }

  onChangeFlightTimeClick() {
    if (this.showSlots) {
      this.showSlots = false;
      // this.bookingSlotChangeStateService.reset();
    }
    this.slotMoveResult = '';
    this.showSlots = true;
    this.loadingSpinnerService.show();
    this.bookingSlotChangeStateService.initialize(this._order.id, this._order.bookingDate).subscribe(
      done => {
        this.loadingSpinnerService.hide();
        this.logger.info('OrderDetailsComponent: bookingSlotChangeStateService initialized.');
      },
      error => {
        this.loadingSpinnerService.hide();
        this.logger.error(`OrderDetailsComponent: bookingSlotChangeStateService reinitialized error: ${error.message}.`);
      }
    );
  }

  onClickTimeSlot(bookingSlot: BookingSlot) {
    if (this.actionInProgress) {
      return;
    }
    // TODOS
    // Group events logic
    // Refactor service to calendar-slots service
    // Load restrictions

    const order = this.orderStateService.order;

    const isChangeTimeSlotValid = this.bookingSlotChangeStateService.validateChangeTimeSlot(order, bookingSlot);

    if (!isChangeTimeSlotValid) {
      this.showErrorMoveTicketPopUp();
      return;
    }

    this.showMoveTicketPopUp(order, bookingSlot);
  }

  get selectedDate(): Date {
    return this.bookingSlotChangeStateService.selectedDate;
  }

  get displayTimeSlots(): BookingSlot[] {
    return this.bookingSlotChangeStateService.displayTimeSlots;
  }

  nextDayClick() {
    this.bookingSlotChangeStateService.shiftDay(1);
  }

  prevDayClick() {
    this.bookingSlotChangeStateService.shiftDay(-1);
  }

  showMoveTicketPopUp(order: Order, bookingSlot: BookingSlot) {

    const dialogRef = this.dialog.open(MessageComponent, {
      data: {
        message: 'Move ticket question',
        noCaption: 'Abort',
        yesCaption: 'Confirm'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.actionInProgress = true;
        this.slotMoveResult = '';
        this.loadingSpinnerService.show();
        this.orderStateService.changeOrderTimeSlot(order, bookingSlot).subscribe(
          result2 => {
            if (result2.message) {
              this.slotMoveResult = result2.message;
            }

            if (result2.result) {
              this.showSlots = false;
              this.bookingSlotChangeStateService.reset();
              this._loadOrder().subscribe(res => {
                this.loadingSpinnerService.hide();
                this.actionInProgress = false;
              });
            }
          },
          error => {
            this.slotMoveResult = 'Order move error';
            this.actionInProgress = false;
            this.loadingSpinnerService.hide();
          }
        );
      }
    });
  }

  showErrorMoveTicketPopUp() {
    const dialogRef = this.dialog.open(MessageComponent, {
      data: {
        message: 'Shift not possible (reason: price difference)',
        noCaption: 'Abort',
        hideYesButton: true,
      }
    });
  }

  formatDatetime(date: Date): string {
    return this.localizationService.formatDatetime(date);
  }

  onCancelOrderClick() {
    if (!this._order || !this.canBeRefunded || this._order.totalAmount === 0) {
      return;
    }

    this.loadingSpinnerService.show();
    this.refundOrderSubscription = this.orderStateService.refundOrder(this.orderId).pipe(
      switchMap(result => {
        if (result) {
          if (result.result && this._needToUpdatePartner) {
            return this.reloadPartner().pipe(
              switchMap(__ => {
                return of(result);
              })
            )
          }
          return of(result);
        }
      }),
    ).subscribe(
      result => {
        if (result) {
          if (result.result) {
            this.router.navigateByUrl('/partner/orders');
          }
          if (!result.result && result.message) {
            this.notificationService.show(OrderDetailsComponent.name, result.message);
          }
        }
        this.loadingSpinnerService.hide();
      },
      error => {
        this.logger.error(error);
        this.notificationService.show(OrderDetailsComponent.name, error.message ? error.message : error);
        this.loadingSpinnerService.hide();
      }
    );
  }

  get isEndDateHide() {
    return this.shopSettingsService.hideDateEndInOrderDetail;
  }

  get isGiftCardOrderType(): boolean {
    return this._order && this._order.orderType === OrderTypeEnum.Giftcard;
  }

  reloadPartner(): Observable<Partner> {
    return this.partnerStateService.reloadPartner()
  }

  private get _needToUpdatePartner(): boolean {
    return this.shopSettingsService.shopSettings.partnerCodeAuthorization && !!this.partnerStateService.loggedInPartner
  }
}
